import FormLabel from '@rio-cloud/rio-uikit/FormLabel';
import classNames from 'classnames';
import type { CSSProperties, Ref, TextareaHTMLAttributes } from 'react';
import { type Control, type ControllerRenderProps, type FieldValues, useController } from 'react-hook-form';

import { ValidationViolation } from './ValidationViolation.tsx';

type ControlledTextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
    ref?: Ref<HTMLTextAreaElement>;
    name: string;
    label?: string;
    // biome-ignore lint/suspicious/noExplicitAny: react-hook-form control accepts generic form values
    control: Control<any>;
    formGroupClassName?: string;
    inputGroupClassName?: string;
};

type TextareaFieldProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
    field: ControllerRenderProps<FieldValues, string>;
    error?: string;
    inputGroupClassName?: string;
    wrapperClassName?: string;
    wrapperStyle?: CSSProperties;
    feedbackWrapperStyle?: CSSProperties;
    textareaStyle?: CSSProperties;
    forwardedRef?: Ref<HTMLTextAreaElement>;
    wrapInputGroup?: boolean;
};

export const TextareaField = ({
    field,
    error,
    inputGroupClassName = 'margin-y-10',
    wrapperClassName,
    wrapperStyle,
    feedbackWrapperStyle,
    textareaStyle,
    forwardedRef,
    wrapInputGroup = true,
    className,
    ...props
}: TextareaFieldProps) => (
    <>
        <div
            className={classNames(
                wrapInputGroup ? classNames('input-group', inputGroupClassName) : undefined,
                wrapperClassName
            )}
            style={wrapperStyle}
        >
            <div className='form-control-feedback-wrapper' style={feedbackWrapperStyle}>
                <textarea
                    {...props}
                    {...field}
                    style={{ ...props.style, ...textareaStyle }}
                    ref={element => {
                        field.ref(element);
                        if (typeof forwardedRef === 'function') {
                            forwardedRef(element);
                        } else if (forwardedRef) {
                            forwardedRef.current = element;
                        }
                    }}
                    className={classNames('form-control', className)}
                />
            </div>
        </div>
        <ValidationViolation violations={error} />
    </>
);

export const ControlledTextarea = ({
    name,
    label,
    control,
    className,
    formGroupClassName,
    inputGroupClassName = 'margin-y-10',
    ref,
    ...props
}: ControlledTextareaProps) => {
    const { field, fieldState } = useController({ control, name });
    const id = props.id ?? name;

    return (
        <div
            className={classNames('form-group', formGroupClassName, {
                'has-error': fieldState.invalid,
                'has-feedback': fieldState.invalid,
            })}
        >
            {label && <FormLabel htmlFor={id}>{label}</FormLabel>}
            <TextareaField
                {...props}
                id={id}
                field={field}
                error={fieldState.error?.message}
                className={className}
                inputGroupClassName={inputGroupClassName}
                forwardedRef={ref}
            />
        </div>
    );
};
